import { useState, useEffect, useMemo } from 'react'
import { Percent, DollarSign, CheckSquare, Square } from 'lucide-react'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { cn } from '@/utils/cn'
import { formatCurrency } from '@/utils/format-currency'
import { getItemMonthlyValue, calculateVariationPricing } from '@/utils/dealCalculations'
import { buildDefaultComboPricing } from '@/utils/comboVariationPricing'
import { ComboVariationPricingFields } from '@/components/Deals/ComboVariationPricingFields'
import type { ComboVariation, PropostaItem } from '@/types'

interface ComboVariationModalProps {
  open: boolean
  variation: ComboVariation | null
  allItems: PropostaItem[]
  onSave: (variation: ComboVariation) => void
  onClose: () => void
}

type DiscountType = 'none' | 'percentual' | 'fechado'

function generateId() {
  return `var-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
}

function formatItemListValue(item: PropostaItem): string {
  const monthly = getItemMonthlyValue(item)
  if (monthly > 0) return `${formatCurrency(monthly)}/mês`
  if (item.tipo === 'avulso') return formatCurrency(item.total || 0)
  return formatCurrency(item.valorUnitario || item.total || 0)
}

export function ComboVariationModal({
  open,
  variation,
  allItems,
  onSave,
  onClose,
}: ComboVariationModalProps) {
  const [nome, setNome] = useState('')
  const [descricao, setDescricao] = useState('')
  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([])
  const [discountType, setDiscountType] = useState<DiscountType>('none')
  const [descontoPercentual, setDescontoPercentual] = useState<number | ''>(0)
  const [precoFechado, setPrecoFechado] = useState<number | ''>(0)
  const [pricingDraft, setPricingDraft] = useState<ComboVariation['pricing']>()
  const [pricingModel, setPricingModel] = useState<ComboVariation['pricingModel']>('recorrente')

  useEffect(() => {
    if (variation) {
      setNome(variation.nome)
      setDescricao(variation.descricao || '')
      setSelectedItemIds([...variation.itemIds])
      if (variation.precoFechado !== undefined) {
        setDiscountType('fechado')
        setPrecoFechado(variation.precoFechado)
      } else if (variation.descontoPercentual && variation.descontoPercentual > 0) {
        setDiscountType('percentual')
        setDescontoPercentual(variation.descontoPercentual)
      } else {
        setDiscountType('none')
      }
      setPricingDraft(variation.pricing)
      setPricingModel(variation.pricingModel ?? 'recorrente')
    } else {
      setNome('')
      setDescricao('')
      setSelectedItemIds([])
      setDiscountType('none')
      setDescontoPercentual(0)
      setPrecoFechado(0)
      setPricingDraft(undefined)
      setPricingModel('recorrente')
    }
  }, [variation, open])

  const selectedItems = useMemo(
    () => allItems.filter((i) => selectedItemIds.includes(i.id)),
    [allItems, selectedItemIds],
  )

  const subtotalMensal = useMemo(
    () => selectedItems.reduce((acc, i) => acc + getItemMonthlyValue(i), 0),
    [selectedItems],
  )

  const setupTotal = useMemo(
    () =>
      selectedItems
        .filter((i) => i.tipo === 'avulso')
        .reduce((acc, i) => acc + (i.total || 0), 0),
    [selectedItems],
  )

  const valorFinal = useMemo(() => {
    if (discountType === 'fechado' && typeof precoFechado === 'number' && precoFechado > 0) {
      return Math.min(precoFechado, subtotalMensal)
    }
    if (
      discountType === 'percentual' &&
      typeof descontoPercentual === 'number' &&
      descontoPercentual > 0
    ) {
      return subtotalMensal * (1 - descontoPercentual / 100)
    }
    return subtotalMensal
  }, [subtotalMensal, discountType, precoFechado, descontoPercentual])

  const draftVariation = useMemo((): ComboVariation => ({
    id: variation?.id || 'draft',
    nome: nome.trim() || 'Combo',
    descricao: descricao.trim() || undefined,
    itemIds: selectedItemIds,
    descontoPercentual: discountType === 'percentual' ? Number(descontoPercentual) : undefined,
    precoFechado: discountType === 'fechado' ? Number(precoFechado) : undefined,
    pricingModel,
    pricing: pricingDraft,
  }), [variation?.id, nome, descricao, selectedItemIds, discountType, descontoPercentual, precoFechado, pricingModel, pricingDraft])

  const pricedDraft = useMemo(
    () => calculateVariationPricing(draftVariation, allItems),
    [draftVariation, allItems],
  )

  const toggleItem = (id: string) => {
    setSelectedItemIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    )
  }

  const handleSave = () => {
    if (!nome.trim()) return
    const priced = calculateVariationPricing(draftVariation, allItems)
    const saved: ComboVariation = {
      ...draftVariation,
      id: variation?.id || generateId(),
      nome: nome.trim(),
      pricing: pricingDraft ?? buildDefaultComboPricing(priced),
    }
    onSave(saved)
  }

  const discountOptions: { value: DiscountType; label: string; icon: React.ReactNode }[] = [
    { value: 'none', label: 'Sem desconto', icon: null },
    { value: 'percentual', label: 'Percentual (%)', icon: <Percent size={12} /> },
    { value: 'fechado', label: 'Preço Fechado', icon: <DollarSign size={12} /> },
  ]

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={variation ? `Editar: ${variation.nome}` : 'Nova Variação de Combo'}
      size='lg'
      className='!max-w-lg'
      footer={
        <>
          <Button variant='ghost' size='sm' onClick={onClose}>
            Cancelar
          </Button>
          <Button
            size='sm'
            onClick={handleSave}
            disabled={!nome.trim() || selectedItemIds.length === 0}
          >
            {variation ? 'Salvar Alterações' : 'Criar Variação'}
          </Button>
        </>
      }
    >
      <div className='flex flex-col gap-4 -mt-1'>
        <div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
          <Input
            label='Nome da Variação *'
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            placeholder='Ex: Combo Start'
          />
          <Input
            label='Descrição (opcional)'
            value={descricao}
            onChange={(e) => setDescricao(e.target.value)}
            placeholder='Ex: Ideal para empresas...'
          />
        </div>

        <div className='space-y-1.5'>
          <div className='flex items-center justify-between'>
            <span
              className='font-medium uppercase tracking-wider text-[var(--font-color-label)]'
              style={{ fontSize: 'var(--font-size-label)' }}
            >
              Itens do Combo
            </span>
            <span className='text-xs text-text-muted'>{selectedItemIds.length} selecionados</span>
          </div>
          {allItems.length === 0 ? (
            <p className='py-2 text-xs italic text-text-muted'>Nenhum item na proposta ainda.</p>
          ) : (
            <div className='max-h-36 space-y-1 overflow-y-auto pr-1'>
              {allItems.map((item) => {
                const selected = selectedItemIds.includes(item.id)
                return (
                  <button
                    key={item.id}
                    type='button'
                    onClick={() => toggleItem(item.id)}
                    className={cn(
                      'flex w-full items-center gap-2.5 rounded-lg border px-2.5 py-2 text-left transition-all',
                      selected
                        ? 'border-emerald-500/40 bg-emerald-500/10 text-text-primary'
                        : 'border-[var(--glass-border)] bg-[var(--glass-bg)] text-text-secondary hover:border-[var(--brand-primary)]/30',
                    )}
                  >
                    {selected ? (
                      <CheckSquare size={14} className='shrink-0 text-emerald-600' />
                    ) : (
                      <Square size={14} className='shrink-0 text-text-muted' />
                    )}
                    <span className='flex-1 truncate text-[13px]'>{item.descricao}</span>
                    <span className='shrink-0 text-[11px] font-medium tabular-nums'>
                      {formatItemListValue(item)}
                    </span>
                  </button>
                )
              })}
            </div>
          )}
        </div>

        <div className='space-y-3 rounded-xl border border-[var(--glass-border)] bg-[var(--glass-bg)] p-3'>
          <div className='grid grid-cols-3 gap-2'>
            {discountOptions.map((opt) => (
              <button
                key={opt.value}
                type='button'
                onClick={() => setDiscountType(opt.value)}
                className={cn(
                  'flex items-center justify-center gap-1.5 rounded-md border px-1 py-1.5 text-[11px] font-medium transition-all',
                  discountType === opt.value
                        ? 'border-emerald-500/50 bg-emerald-500/15 text-emerald-700'
                    : 'border-[var(--glass-border)] bg-[var(--bg-card)] text-text-muted hover:border-[var(--brand-primary)]/25',
                )}
              >
                {opt.icon}
                <span className='leading-none'>{opt.label}</span>
              </button>
            ))}
          </div>

          <div className='flex items-end justify-between gap-3'>
            <div className='w-32'>
              {discountType === 'percentual' && (
                <Input
                  type='number'
                  min={0}
                  max={100}
                  step={1}
                  value={descontoPercentual}
                  onChange={(e) =>
                    setDescontoPercentual(e.target.value === '' ? '' : Number(e.target.value))
                  }
                  iconRight={<span className='text-xs text-text-muted'>%</span>}
                />
              )}
              {discountType === 'fechado' && (
                <Input
                  type='number'
                  min={0}
                  step={100}
                  value={precoFechado}
                  onChange={(e) =>
                    setPrecoFechado(e.target.value === '' ? '' : Number(e.target.value))
                  }
                  icon={<span className='text-xs text-text-muted'>R$</span>}
                />
              )}
            </div>

            <div className='flex-1 rounded-md border border-[var(--glass-border)] bg-[var(--bg-card)] px-3 py-1.5 text-right'>
              {discountType !== 'none' && subtotalMensal !== valorFinal ? (
                <div className='flex flex-col items-end gap-0.5'>
                  <div className='flex items-center justify-between gap-2 w-full'>
                    <span className='text-xs text-text-muted line-through tabular-nums'>
                      {formatCurrency(subtotalMensal)}/mês
                    </span>
                    <span className='text-sm font-bold text-emerald-700 tabular-nums'>
                      {formatCurrency(valorFinal)}/mês
                    </span>
                  </div>
                  {setupTotal > 0 && (
                    <span className='text-[10px] text-text-muted tabular-nums'>
                      + setup {formatCurrency(setupTotal)}
                    </span>
                  )}
                </div>
              ) : (
                <div className='flex flex-col items-end gap-0.5'>
                  <div className='flex items-center justify-between gap-2 w-full'>
                    <span className='text-xs text-text-muted'>Valor Mensal</span>
                    <span className='text-sm font-bold text-text-primary tabular-nums'>
                      {formatCurrency(subtotalMensal)}/mês
                    </span>
                  </div>
                  {setupTotal > 0 && (
                    <span className='text-[10px] text-text-muted tabular-nums'>
                      + setup {formatCurrency(setupTotal)}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {selectedItemIds.length > 0 ? (
          <ComboVariationPricingFields
            variation={draftVariation}
            priced={pricedDraft}
            onChange={(patch) => {
              if (patch.pricingModel) setPricingModel(patch.pricingModel)
              if (patch.pricing !== undefined) setPricingDraft(patch.pricing)
            }}
          />
        ) : null}
      </div>
    </Modal>
  )
}
